import { useState } from "react";
import { CheckCircle2, Download, MessageSquare } from "lucide-react";
import { GuideModal } from "./GuideModal";

export const AboutSection = () => {
  const [guideModalOpen, setGuideModalOpen] = useState(false);

  const supportItems = [
    "Downsizing to simplify life",
    "Preparing for a future move — even years ahead",
    "Planning extended travel or living abroad",
    "Relocating closer to family or support",
    "Helping a loved one navigate housing decisions"
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section id="about" className="section bg-[#F8FAFC]" data-testid="about-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="animate-hidden animate-slideInLeft">
            <div className="gold-line" />
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-8 font-serif leading-tight">
              Making a Move Later in Life Deserves a Thoughtful Approach
            </h2>
            <p className="text-lg text-[#475569] mb-6 leading-relaxed">
              Making a housing decision later in life isn't just about buying or selling a home. It's about timing, comfort, finances, and peace of mind.
            </p>
            <p className="text-lg text-[#475569] mb-8 leading-relaxed">
              Whether you're downsizing, relocating closer to family, planning extended travel, or simply thinking ahead, you deserve clear information and respectful guidance — without feeling rushed or pressured.
            </p>
            <p className="text-lg text-[#0F172A] font-medium mb-8 leading-relaxed">
              Next Chapter Moves NC was created to support homeowners who want to understand their options first, and move forward only when the time feels right.
            </p>

            <h3 className="text-xl font-semibold text-[#0F172A] mb-4 font-serif">
              Support for Every Kind of Next Chapter
            </h3>
            <p className="text-base text-[#475569] mb-6">
              Next Chapter Moves NC is designed for homeowners who are:
            </p>
            <ul className="space-y-3 mb-8">
              {supportItems.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#D4AF37] mt-1 flex-shrink-0" size={20} />
                  <span className="text-[#475569]">{item}</span>
                </li>
              ))}
            </ul>

            <p className="text-base text-[#475569] italic">
              Every situation is different. The approach remains the same: calm, clear, and education-first.
            </p>
          </div>

          <div className="animate-hidden animate-slideInRight animate-delay-200">
            <div className="image-container rounded-sm overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1554331292-735256644d5f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxzZW5pb3IlMjBjb3VwbGV8ZW58MHx8fHwxNzcwMjMxNDI0fDA&ixlib=rb-4.1.0&q=85"
                alt="Senior couple enjoying their home"
                className="w-full h-[500px] object-cover"
                loading="lazy"
                data-testid="about-image"
              />
            </div>
          </div>
        </div>

        <div className="mt-20 bg-[#0F172A] p-10 md:p-16 text-center">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 font-serif">
            Start With Information — Not a Sales Pitch
          </h3>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            If you'd like to explore your options without pressure, you can start with a free guide or a simple conversation. Both are designed to help you feel informed, comfortable, and in control — no cold calls, no obligation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="#contact" 
              onClick={(e) => scrollToSection(e, "#contact")}
              className="btn-primary"
            >
              Start a Next Chapter Conversation
              <MessageSquare size={18} />
            </a>
            <button 
              onClick={() => setGuideModalOpen(true)}
              className="btn-secondary"
            >
              <Download size={18} />
              Download Free Guide
            </button>
          </div>
        </div>
      </div>

      <GuideModal isOpen={guideModalOpen} onClose={() => setGuideModalOpen(false)} />
    </section>
  );
};
